import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Txt } from 'components/Txt';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { isEqual } from 'lodash';
import * as React from 'react';
import { Keyboard } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  SET_INCOMING_VALUE,
} from 'store/app/actions';
import { selectIncomingValue } from 'store/app/selectors';
import { REMOTE_CREATE_ASSET } from 'store/remote/actions';
import { APIRequestCreateAsset } from 'store/remote/types';

export const CreateAssetScreen: React.FC<
  ModalScreenProps<ModalScreens.CREATEASSET>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const { organisations } = route.params;

  const [uState, setUState] = React.useState<APIRequestCreateAsset>({
    name: '',
    organisationId: '',
  });

  const incoming = useSelector((state) =>
    selectIncomingValue(state, 'incomingValueCreateAsset'),
  ) as { organisationId: string } | undefined;

  const isValid = !!uState.name && !!uState.organisationId;

  const handleSetState = (key: string, value: string) => {
    setUState((cState) => ({ ...cState, [key]: value }));
  };

  const handlePressCreate = React.useCallback(() => {
    Keyboard.dismiss();
    dispatch(REMOTE_CREATE_ASSET(uState));
  }, [dispatch, uState]);

  const hasValues = !!uState.name || !!uState.organisationId;

  const handleClose = React.useCallback(() => {
    if (hasValues) {
      confirmUnsaved({
        onSave: handlePressCreate,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handlePressCreate, hasValues]);

  useEsc(handleClose);

  React.useEffect(() => {
    if (incoming) {
      setUState((cState) => {
        const newState = { ...cState, ...incoming };
        return !isEqual(cState, newState) ? newState : cState;
      });
      dispatch(SET_INCOMING_VALUE({ key: 'incomingValueCreateAsset' }));
    }
  }, [dispatch, incoming]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !hasValues });
  }, [navigation, hasValues]);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="New Asset"
          onPressCancelButton={handleClose}
          onPressSaveButton={isValid ? handlePressCreate : null}
          saveButtonText="Create"
        />
      }
    >
      <CardGeneric>
        <Txt>
          Before creating an asset, make sure it doesn&apos;t already exist in
          RiskBase as assessments and other data cannot be moved.
        </Txt>
      </CardGeneric>
      <Card
        explanation={
          <>
            Make sure the correct organisation is chosen; the asset{' '}
            <Txt weight="bold">cannot</Txt> be moved to a different organisation
            later.
          </>
        }
      >
        <CardRow
          label="Organisation"
          value={
            organisations.find((org) => org.id === uState.organisationId)
              ?.name || 'No Organisation Chosen'
          }
          onPress={() =>
            dispatch(
              NAVIGATE_MODAL({
                screen: ModalScreens.SELECT,
                params: {
                  fieldName: 'organisationId',
                  incomingValueKey: 'incomingValueCreateAsset',
                  options: organisations.map((o) => ({
                    name: o.name,
                    internalValue: o.id,
                  })),
                  searchable: true,
                  returnType: 'string',
                  title: 'Choose Organisation',
                  value: uState.organisationId,
                },
              }),
            )
          }
        />
      </Card>
      <Card label="Asset Name">
        <TextField
          value={uState.name}
          onChange={(value) => handleSetState('name', value)}
          autoCapitalize="words"
          onEsc={handleClose}
        />
      </Card>
    </PageScrollView>
  );
};
